/* Session du client, lue de façon synchrone au démarrage : un rechargement de
   page ne repasse plus par la connexion tant que le jeton est valide.

   Le jeton (JWT émis par user-service à /user/client-login) porte sa date
   d'expiration (exp, en secondes). On ne vérifie pas la signature dans le
   navigateur : c'est le rôle des services, qui répondent 401 à un jeton
   invalide (voir api/axios.ts). On lit seulement exp pour savoir quand
   renvoyer le client vers la connexion. */

export interface ClientSession {
  email: string;
  nom: string;
  organisation: string;
}

const CLE_UTILISATEUR = 'clientUser';
const CLE_JETON = 'clientToken';

/* Marge : un jeton qui expire dans moins de 30 s est déjà traité comme expiré. */
const MARGE_MS = 30_000;

/** Date d'expiration du jeton en millisecondes, ou null s'il n'en porte pas (ou est illisible). */
export function expirationDuJeton(jeton: string): number | null {
  const partie = jeton.split('.')[1];
  if (!partie) return null;
  try {
    const base64 = partie.replace(/-/g, '+').replace(/_/g, '/');
    const complet = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const charge = JSON.parse(atob(complet));
    return typeof charge.exp === 'number' ? charge.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Vrai si le jeton est lisible et pas encore expiré. Un jeton sans exp est laissé au jugement des services. */
export function jetonEncoreValide(jeton: string, maintenant = Date.now()): boolean {
  if (jeton.split('.').length !== 3) return false;
  const exp = expirationDuJeton(jeton);
  return exp == null || exp - MARGE_MS > maintenant;
}

export const lireJeton = (): string | null =>
  localStorage.getItem(CLE_JETON) || sessionStorage.getItem(CLE_JETON);

export function enregistrerSession(utilisateur: ClientSession, jeton: string): void {
  localStorage.setItem(CLE_UTILISATEUR, JSON.stringify(utilisateur));
  localStorage.setItem(CLE_JETON, jeton);
  // Anciennes copies en sessionStorage (versions précédentes du portail).
  sessionStorage.removeItem(CLE_UTILISATEUR);
  sessionStorage.removeItem(CLE_JETON);
}

export function effacerSession(): void {
  for (const stockage of [localStorage, sessionStorage]) {
    stockage.removeItem(CLE_UTILISATEUR);
    stockage.removeItem(CLE_JETON);
  }
}

/** Session enregistrée, si le jeton est encore valide ; sinon efface tout et renvoie null. */
export function lireSession(maintenant = Date.now()): { utilisateur: ClientSession; jeton: string } | null {
  const jeton = lireJeton();
  const brut = localStorage.getItem(CLE_UTILISATEUR) || sessionStorage.getItem(CLE_UTILISATEUR);
  if (!jeton || !brut || !jetonEncoreValide(jeton, maintenant)) {
    if (jeton || brut) effacerSession();
    return null;
  }
  try {
    const u = JSON.parse(brut);
    if (!u || typeof u.email !== 'string' || !u.email) throw new Error('session illisible');
    return {
      jeton,
      utilisateur: { email: u.email, nom: typeof u.nom === 'string' ? u.nom : '', organisation: typeof u.organisation === 'string' ? u.organisation : '' },
    };
  } catch {
    effacerSession();
    return null;
  }
}

/** Chemin interne sûr où revenir après la connexion (jamais une autre origine). */
export function cheminDeRetour(valeur: unknown): string {
  if (typeof valeur !== 'string' || !valeur.startsWith('/') || valeur.startsWith('//') || valeur.startsWith('/\\')) return '/';
  if (valeur.startsWith('/connexion') || valeur.startsWith('/inscription')) return '/';
  return valeur;
}

/** Fin de session décidée par un service (401) : retour à la connexion, page demandée conservée. */
export function terminerSessionExpiree(): void {
  effacerSession();
  const { pathname, search } = window.location;
  if (pathname.startsWith('/connexion')) return;
  const retour = encodeURIComponent(cheminDeRetour(pathname + search));
  window.location.assign(`/connexion?expiree=1&retour=${retour}`);
}
